import React, { useCallback, useEffect, createContext, useState } from 'react';
import * as http from 'utils/http';
import config from 'config';
import serialize from 'utils/serialize';
import { normalize } from 'utils/converters';
import useSWR from 'swr';

const getJourneysByDriver = async (query) =>
  http.get(`${config.API_URL}assignment/journeys/by_driver/?${query}`);

const getServiceFleetDrivers = ({ routesFilter }) => {
  return `mercury/drivers/?${serialize(serviceFleetFilters({ routes: routesFilter }))}`;
};

const fetcher = (url) => http.get(`${config.API_URL}${url}`);

const MyFleetContext = createContext();

const serviceFleetFilters = ({ routes = [] }) => ({
  is_fleet_service: true,
  page_size: 0,
  route: routes?.map((routeFilter) => `${routeFilter.value}->${routeFilter.value}`).join(','),
});

function MyFleetContextProvider({ children, filters }) {
  const [mounted, setMounted] = useState(true);
  const [isAddMode, setIsAddMode] = useState(false);
  const setMyFleet = useCallback(() => {
    /*
     * Noop, to be implemented if we want to modify
     * the service fleet from this module
     * */
  }, []);

  const [myFleetObject, setMyFleetObject] = useState({});
  const [fetchingJourneys, setFetchingJourneys] = useState(false);
  const [journeysByDriver, setJourneysByDriver] = useState({});
  const [drivers, setDrivers] = useState('');

  const dateFilter = (filters || {}).date;
  const routesFilter = (filters || {}).routes;

  const { data: myFleet, error: myFleetError } = useSWR(
    getServiceFleetDrivers({ routesFilter }),
    fetcher
  );

  const handleZeroResultsAdd = useCallback(() => {
    setIsAddMode(true);
  }, []);

  const handleCancel = useCallback(() => {
    setIsAddMode(false);
  }, []);

  const handleSubmit = useCallback(
    (e, data) => {
      setMyFleet([data, ...myFleet]);
    },
    [myFleet, setMyFleet]
  );

  const handleRemoveCard = useCallback(
    (filterId) => {
      const myFleetNew = (myFleet || []).filter(({ id }) => id !== filterId);
      setMyFleet(myFleetNew);
    },
    [myFleet, setMyFleet]
  );

  const refreshJourneysByDriver = useCallback(
    async (driverId) => {
      const query = serialize({
        pickup_time_from: dateFilter,
        drivers: [driverId],
      });

      const data = await getJourneysByDriver(query);

      setJourneysByDriver({ ...journeysByDriver, ...data });
    },
    [dateFilter, journeysByDriver]
  );

  useEffect(() => {
    setMyFleetObject(normalize(myFleet));
  }, [myFleet]);

  useEffect(() => {
    const savedDrivers = (myFleet || [])?.map(({ id }) => id);
    setDrivers(savedDrivers.join(','));
  }, [myFleet]);

  useEffect(() => {
    async function _fetchJourneys(pickupTimeFrom, driversToFetch) {
      if (!mounted || !pickupTimeFrom || !driversToFetch) {
        return;
      }

      setFetchingJourneys(true);

      const journeyFilters = serialize({
        pickup_time_from: pickupTimeFrom,
        drivers: driversToFetch,
      });

      try {
        const journeys = await getJourneysByDriver(journeyFilters);

        mounted && setJourneysByDriver(journeys);
      } catch (err) {
      } finally {
        mounted && setFetchingJourneys(false);
      }
    }

    _fetchJourneys(dateFilter, drivers);
  }, [mounted, dateFilter, drivers]);

  useEffect(() => {
    return () => setMounted(false);
  }, []);

  return (
    <MyFleetContext.Provider
      value={{
        isAddMode,
        onSubmit: handleSubmit,
        onCancel: handleCancel,
        onZeroResultsAdd: handleZeroResultsAdd,
        removeCard: handleRemoveCard,
        myFleet,
        myFleetObject,
        fetchingJourneys,
        journeysByDriver,
        refreshJourneysByDriver,
        isLoading: myFleet === undefined && !myFleetError,
      }}
    >
      {children}
    </MyFleetContext.Provider>
  );
}

export { MyFleetContextProvider };
export default MyFleetContext;
